
import { computed, ref } from "vue";
import DatePicker from "@/components/Form/DatePicker.vue";
import { Student } from "@/interfaces/api";
import SsnService from "@/services/social-security-number/ssn-service";
import { useI18n } from "vue-i18n";
import DateHelper from "@/helpers/date-helper";
import store from "@/store";

export default {
  name: "Admin",
  components: {
    DatePicker,
  },
  setup(): unknown {
    const { t } = useI18n();
    const person = ref<Student | undefined>(undefined);
    const day = ref<string>("");
    const feedbackMessage = ref<string>("");
    const startDate = ref<string | null>("");

    const getCurrentDateString = (): string => {
      return DateHelper.formatDate(new Date());
    };

    const endDate = ref<string | null>(getCurrentDateString());

    const downloadExcel = async () => {
      store.commit("showSpinner", true);

      const response = await SsnService.exportStudentsToExcel(
        formattedStartDate.value,
        formattedEndDate.value
      )
        .catch((error: Error) => {
          handleDownloadFeedback(error.message);
        })
        .finally(() => {
          store.commit("showSpinner", false);
        });

      if (!response) {
        handleDownloadFeedback();
      }
    };

    const handleDownloadFeedback = (error = ""): void => {
      const excelDownloadError = computed(() =>
        t("shared.download-excel-error")
      ).value;

      if (error) {
        feedbackMessage.value = `${excelDownloadError}: ${error}`;
      } else {
        feedbackMessage.value = excelDownloadError;
      }
    };

    const handleStartDateChange = (event: Event) => {
      const input = event.target as HTMLInputElement;
      startDate.value = input.value;
    };

    const handleEndDateChange = (event: Event) => {
      const input = event.target as HTMLInputElement;
      endDate.value = input.value;
    };

    const areDatesValid = computed(() => {
      if (!startDate.value || !endDate.value) return true;
      return (
        DateHelper.dateAisBeforeDateB(startDate.value, endDate.value) ||
        DateHelper.datesAreSame(startDate.value, endDate.value)
      );
    });

    const areDatesFilled = computed(() => {
      return startDate.value && endDate.value;
    });

    const formattedStartDate = computed<string | null>({
      get() {
        return DateHelper.formatDate(startDate.value);
      },
      set(value: string | null) {
        startDate.value = value;
      },
    });

    const formattedEndDate = computed({
      get() {
        return DateHelper.formatDate(endDate.value);
      },
      set(value: string) {
        endDate.value = value;
      },
    });

    /*
     * Disables selection of future dates in the 'To' date picker
       Returns a boolean value based on whether the input date is in the future (after the current date).
     */
    const futureDates = computed(() => {
      return (date: Date): boolean => {
        return date > new Date();
      };
    });

    return {
      t,
      person,
      day,
      feedbackMessage,
      startDate,
      endDate,
      areDatesValid,
      areDatesFilled,
      downloadExcel,
      handleStartDateChange,
      handleEndDateChange,
      formattedStartDate,
      formattedEndDate,
      futureDates: futureDates,
    };
  },
};
